import {analytics} from '@/Analytics/analytics';
import type {Props} from '@/Analytics/types';

/**
 * Telemetry for the "puzzle delivered late / screen froze" investigation.
 * Timeline per student: push received -> get_active_puzzle -> puzzle rendered.
 * Every event carries the same context so one student's timeline can be
 * pulled by user_id + meeting_id (ids only, no PII).
 */

type Context = {
  userId?: string | number;
  meetingId?: string;
  occurenceId?: string;
  role?: string;
};

// Distinguishes several tabs of the same user (the socket server only routes
// pushes to one "active" tab per user).
const TAB_ID = Math.random().toString(36).slice(2, 8);
const LONGTASK_WINDOW_MS = 30000;

let ctx: Context = {};
let lastPushAt: number | null = null;
let longTaskStop: (() => void) | null = null;

export const setPuzzleTelemetryContext = (next: Context) => {
  ctx = {...ctx, ...next};
};

export const msSincePush = (): number | null =>
  lastPushAt == null ? null : Date.now() - lastPushAt;

const base = (): Props => ({
  user_id: ctx.userId,
  meeting_id: ctx.meetingId,
  occurence_id: ctx.occurenceId,
  role: ctx.role,
  tab_id: TAB_ID,
  visibility:
    typeof document !== 'undefined' ? document.visibilityState : undefined,
  online: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
  ms_since_push: msSincePush(),
});

export const trackPuzzle = (name: string, props: Props = {}) => {
  analytics.track(name, {...base(), ...props});
};

export const trackPuzzleError = (
  name: string,
  err: unknown,
  props: Props = {},
) => {
  analytics.error(err, {event: name, ...base(), ...props});
};

/**
 * Counts main-thread blocks (>50ms) for 30s after a push, to tell "screen
 * froze" apart from "puzzle arrived late". Browser-only; no-op elsewhere.
 */
const watchLongTasks = () => {
  longTaskStop?.();
  longTaskStop = null;
  if (typeof PerformanceObserver === 'undefined') return;
  let count = 0;
  let total = 0;
  let max = 0;
  let observer: PerformanceObserver;
  try {
    observer = new PerformanceObserver(list => {
      list.getEntries().forEach(e => {
        count += 1;
        total += e.duration;
        max = Math.max(max, e.duration);
      });
    });
    observer.observe({entryTypes: ['longtask']});
  } catch (e) {
    return; // longtask not supported (e.g. Safari/Firefox)
  }
  const finish = () => {
    clearTimeout(timer);
    observer.disconnect();
    longTaskStop = null;
    trackPuzzle('classroom_puzzle_longtasks', {
      count,
      total_ms: Math.round(total),
      max_ms: Math.round(max),
      window_ms: LONGTASK_WINDOW_MS,
    });
  };
  const timer = setTimeout(finish, LONGTASK_WINDOW_MS);
  longTaskStop = finish;
};

const TIMING_WAIT_MS = 2000;
const NO_TIMING = {report: (_name: string, _props?: Props) => {}};

const r = (n: number) => Math.round(n);

/**
 * Splits one request's wait into browser queue / DNS / connect / server /
 * download using the browser's resource timing, to tell "slow server" from
 * "request sat in the browser or on the network". Start it right before the
 * request is sent; call report() once the request has settled. Entries arrive
 * a moment after the response, so report() waits briefly for it.
 *
 * Cross-origin responses without a Timing-Allow-Origin header only expose
 * start/end, so the phase split is reported only when requestStart is set.
 * Browser-only; no-op elsewhere.
 */
export const watchRequestTiming = (urlPart: string) => {
  if (
    typeof PerformanceObserver === 'undefined' ||
    typeof performance === 'undefined'
  ) {
    return NO_TIMING;
  }
  const sentAt = performance.now();
  let entry: PerformanceResourceTiming | null = null;
  let seen = 0;
  let observer: PerformanceObserver;
  let onEntry: (() => void) | null = null;
  try {
    observer = new PerformanceObserver(list => {
      list.getEntries().forEach(e => {
        if (e.name.includes(urlPart) && e.startTime >= sentAt - 1) {
          entry = e as PerformanceResourceTiming;
          seen += 1;
        }
      });
      if (entry) onEntry?.();
    });
    observer.observe({type: 'resource'});
  } catch (e) {
    return NO_TIMING;
  }

  const timingProps = (e: PerformanceResourceTiming): Props => {
    const props: Props = {
      timing_found: true,
      timing_entries: seen,
      lead_ms: r(e.startTime - sentAt),
      total_ms: r(e.duration),
      protocol: e.nextHopProtocol || undefined,
    };
    if (e.requestStart > 0) {
      const preConnect = e.domainLookupStart || e.connectStart || e.requestStart;
      props.stalled_ms = r(preConnect - e.fetchStart);
      props.dns_ms = r(e.domainLookupEnd - e.domainLookupStart);
      props.connect_ms = r(e.connectEnd - e.connectStart);
      props.server_ms = r(e.responseStart - e.requestStart);
      props.download_ms = r(e.responseEnd - e.responseStart);
    }
    return props;
  };

  return {
    report: (name: string, props: Props = {}) => {
      let done = false;
      const emit = () => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        onEntry = null;
        observer.disconnect();
        trackPuzzle(name, {
          ...props,
          ...(entry ? timingProps(entry) : {timing_found: false}),
        });
      };
      const timer = setTimeout(emit, TIMING_WAIT_MS);
      if (entry) {
        emit();
      } else {
        onEntry = emit;
      }
    },
  };
};

/** Call when the classroom_puzzle_created socket push arrives. */
export const markPuzzlePush = (extra: Props = {}) => {
  lastPushAt = Date.now();
  trackPuzzle('classroom_puzzle_push_received', extra);
  watchLongTasks();
};

/** Call once the puzzle is on screen for this student. */
export const markPuzzleRendered = (puzzleId: string | number, extra: Props = {}) => {
  const ms = msSincePush();
  trackPuzzle('classroom_puzzle_rendered', {puzzle_id: puzzleId, ...extra});
  if (ms != null) {
    analytics.measure('classroom_puzzle_push_to_render_ms', ms, {
      ...base(),
      puzzle_id: puzzleId,
    });
  }
};

/** Resets module state; tests only. */
export const _resetPuzzleTelemetry = () => {
  ctx = {};
  lastPushAt = null;
  longTaskStop = null;
};
